import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils.ts";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-display font-semibold transition-[transform,filter,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.972]",
  {
    variants: {
      variant: {
        // Signature gold — warm gradient pour, espresso text, amber glow.
        default:
          "bg-[linear-gradient(180deg,var(--gold-soft),var(--gold-500)_55%,var(--gold-deep))] text-primary-foreground shadow-[0_10px_26px_-10px_var(--amber-glow),inset_0_1px_0_oklch(100%_0_0_/_40%)] hover:brightness-[1.04]",
        // "Cheers" lime — the go/send accent.
        accent:
          "bg-[linear-gradient(180deg,oklch(90%_0.16_120),var(--lime-500)_55%,var(--lime-deep))] text-accent-foreground shadow-[0_10px_26px_-10px_oklch(86%_0.18_120_/_40%),inset_0_1px_0_oklch(100%_0_0_/_40%)] hover:brightness-[1.04]",
        secondary: "bg-secondary text-secondary-foreground hover:brightness-[1.04]",
        // Translucent glass — quiet secondary actions.
        ghost:
          "bg-[oklch(100%_0_0_/_4%)] border border-hairline-strong text-cream hover:bg-[oklch(100%_0_0_/_8%)]",
        outline:
          "border border-hairline-strong bg-transparent text-cream hover:bg-[oklch(100%_0_0_/_6%)]",
        quiet: "bg-transparent text-cream-2 hover:text-cream",
        destructive:
          "bg-[oklch(67%_0.16_30_/_8%)] border border-[oklch(67%_0.16_30_/_30%)] text-destructive hover:bg-[oklch(67%_0.16_30_/_14%)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-10 rounded-sm px-4 text-[0.84rem]",
        default: "h-12 px-5 text-[0.95rem]",
        lg: "h-14 px-7 text-base",
        icon: "size-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { buttonVariants };
